"use client"

import { WarningMarkIcon } from "@/app/icons/warning-mark"
import { PriceQuote } from "@/app/offers/price-quote"
import { useOfferValidation } from "@/app/offers/use-offer-validation"
import { useEvmCollectionBid } from "@/app/sdk/action-molecules/evm/evm-collection-bid-molecule"
import type { PriceValue } from "@anaconda/core/price/types"
import { Trans } from "@lingui/react/macro"
import {
  Button,
  Card,
  DialogHeader,
  DialogTitle,
} from "@rarible_int/anaconda_ui_components"
import { atom, computed } from "@reatom/core"
import { reatomComponent } from "@reatom/react"
import { useMemo, useState } from "react"
import {
  useCollection,
  useCollectionNativeCurrency,
  useCollectionStatistic,
  useCollectionWrappedCurrency,
} from "../store/use-collection"
import { CollectionPreview } from "./collection-preview"
import { OfferPriceInput } from "./price-input"

interface EvmModernCollectionOfferDialogContentProps {
  onOpenChange: (isOpened: boolean) => void
}

export const EvmModernCollectionOfferDialogContent =
  reatomComponent<EvmModernCollectionOfferDialogContentProps>(
    ({ onOpenChange }) => {
      const collection = useCollection()
      const [quantity] = useState<number>(1)
      const nativeCurrency = useCollectionNativeCurrency()
      const wrappedCurrency = useCollectionWrappedCurrency()

      const currency = wrappedCurrency ?? nativeCurrency

      const { priceAtom, priceValueComputed } = useMemo(() => {
        const priceAtom = atom<number | undefined>(undefined, "offerPrice")
        const priceValueComputed = computed(() => {
          const price = priceAtom()
          if (!price) {
            return undefined
          }

          const priceValue: PriceValue = {
            amount: price.toString(),
            currency,
          }

          return priceValue
        }, "offerPriceValue")

        return { priceAtom, priceValueComputed }
      }, [currency])

      const price = priceAtom()
      const setPrice = (value: number | undefined) => priceAtom.set(value)

      const collectionStatistic = useCollectionStatistic()
      const { error } = useOfferValidation({
        price,
        quantity,
        currency,
        floor: collectionStatistic?.floor,
      })
      const { mutate } = useEvmCollectionBid()

      return (
        <form
          className="flex flex-col gap-y-2"
          onSubmit={(e) => {
            e.preventDefault()
            if (price === undefined) return
            mutate({
              amount: 1,
              currency: currency.id,
              price,
            })
            onOpenChange(false)
          }}
        >
          <DialogHeader className="pb-2">
            <DialogTitle className="text-center text-lg">
              <Trans>Collection offer</Trans>
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col gap-2">
            <CollectionPreview collection={collection} />
            <OfferPriceInput
              onPriceChange={setPrice}
              error={error}
              currency={currency}
            />
            {wrappedCurrency && (
              <Card className="flex items-center gap-2 p-4">
                <div>
                  <WarningMarkIcon className="flex-shrink-0 flex-grow-0" />
                </div>
                <div>
                  <Trans>
                    Your {nativeCurrency.abbreviation.toUpperCase()} will be
                    swapped to {wrappedCurrency.abbreviation.toUpperCase()} to
                    make an offer. You'll be asked to sign a transaction.
                  </Trans>
                </div>
              </Card>
            )}
            <PriceQuote
              quantity={quantity}
              price={{
                amount: price?.toString() ?? "0",
                currency,
              }}
            />
          </div>
          <Button
            className="flex-grow"
            type="submit"
            disabled={!price || !!error}
          >
            <Trans>Make a collection offer</Trans>
          </Button>
        </form>
      )
    },
    "EvmModernCollectionOfferDialogContent",
  )
